<template>
  <div class="company-dropdown-root">
    <!-- Loading Modal with transition effects -->
    <v-dialog
      v-model="isLoading"
      persistent
      max-width="400"
      :overlay="true"
      :z-index="9999"
      transition="dialog-bottom-transition"
    >
      <v-card class="loading-modal">
        <v-card-text class="pa-6 text-center d-flex flex-column">
          <!-- Company Icons Animation -->
          <div class="company-switch-animation mb-4">
            <v-icon
              size="40"
              color="grey darken-1"
              class="current-company-icon"
            >mdi-domain</v-icon>
            
            <div class="arrow-animation">
              <v-icon color="primary" class="arrow-icon">mdi-arrow-right</v-icon>
            </div>
            
            <v-icon
              size="40"
              color="primary"
              class="target-company-icon"
            >mdi-domain-plus</v-icon>
          </div>
          
          <!-- Custom Progress Bar -->
          <div class="custom-progress-bar mb-4">
            <div class="progress-track">
              <div class="progress-fill"></div>
            </div>
          </div>
          
          <h3 class="text-h5 mb-2 primary--text font-weight-bold">Switching Company</h3>
          
          <div v-if="targetCompany" class="target-company-info mb-3">
            <v-avatar size="32" color="primary" class="white--text mr-2">
              {{ targetCompany.name.charAt(0).toUpperCase() }}
            </v-avatar>
            <span class="text-h6">{{ targetCompany.name }}</span>
          </div>
          
          <v-chip class="ma-2" color="primary" outlined>
            <v-icon left>mdi-sync</v-icon>
            Updating session
          </v-chip>
          
          <p class="text-body-2 mt-4 grey--text text--darken-1">
            <v-icon small class="mr-1">mdi-information-outline</v-icon>
            Your page will reload automatically
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>
    
    <v-menu
      bottom
      left
      offset-y
      :close-on-content-click="false"
      max-width="400"
    >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        class="transparent-btn"
        text
        depressed
        :ripple="false"
        height="20"
        v-bind="attrs"
        v-on="on"
      >
        <v-icon small color="bg_force_white" class="activator-lead-icon">mdi-domain</v-icon>
        <span class="text-truncate" style="max-width: 150px;">{{ currentCompanyName }}</span>
        <v-icon right small color="bg_force_white">mdi-chevron-down</v-icon>
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="py-2 d-flex align-center">
        <span class="text-h6">Companies</span>
        <v-spacer></v-spacer>
        <!-- Back to Management Company Icon Button -->
        <v-tooltip bottom v-if="managementCompany && !isCurrentCompanyManagement">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="selectCompany(managementCompany)"
              class="management-back-icon-btn"
            >
              <v-icon small>mdi-exit-to-app</v-icon>
            </v-btn>
          </template>
          <span>Back to {{ managementCompany.company.name }}</span>
        </v-tooltip>
      </v-card-title>

      <v-card-text class="pb-0">
        <v-text-field
          v-model="companySearch"
          placeholder="Search companies..."
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          clearable
          hide-details
        ></v-text-field>
      </v-card-text>

      <v-list dense max-height="280" class="compact-list py-0" style="overflow-y: auto;">
        <!-- Current Company Section (Always at top, highlighted) -->
        <template v-if="currentCompanyAccessItem && !isCurrentCompanyManagement">
          <v-subheader class="current-section-header compact-subheader">
            Current
          </v-subheader>

          <v-list-item
            :key="'current-' + currentCompanyAccessItem.id"
            class="current-company-item compact-list-item"
            @click="selectCompany(currentCompanyAccessItem)"
          >
            <v-list-item-avatar size="24" class="my-0 mr-2">
              <v-avatar size="24" color="success" class="current-avatar">
                <v-icon x-small color="white">mdi-domain</v-icon>
              </v-avatar>
            </v-list-item-avatar>

            <v-list-item-content class="py-1">
              <v-list-item-title class="d-flex align-center text-body-2">
                <span class="text-truncate mr-1 font-weight-medium">{{ currentCompanyAccessItem.company.name }}</span>
                <v-chip x-small color="success" label class="compact-chip">
                  Active
                </v-chip>
              </v-list-item-title>
            </v-list-item-content>

            <v-list-item-action class="my-0 ml-1">
              <v-btn
                icon
                x-small
                :disabled="!currentCompanyAccessItem.hasAccount"
                @click.stop="toggleFavorite(currentCompanyAccessItem)"
              >
                <v-icon x-small :color="currentCompanyAccessItem.isFavourite ? 'amber' : 'grey'">
                  {{ currentCompanyAccessItem.isFavourite ? 'mdi-star' : 'mdi-star-outline' }}
                </v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>

          <v-divider class="my-1 company-separator"></v-divider>
        </template>

        <!-- Management Company Section (Highlighted) -->
        <template v-if="managementCompany">
          <v-subheader class="management-section-header compact-subheader">
            Management
            <v-chip
              v-if="isCurrentCompanyManagement"
              x-small
              color="success"
              label
              class="ml-1 compact-chip"
            >
              Active
            </v-chip>
          </v-subheader>

          <v-list-item
            :key="managementCompany.id"
            @click="selectCompany(managementCompany)"
            class="compact-list-item"
            :class="{
              'management-company-item': true,
              'management-company-item--active': isCurrentCompanyManagement
            }"
          >
            <v-list-item-avatar size="24" class="my-0 mr-2">
              <v-avatar size="24" :color="isCurrentCompanyManagement ? 'success' : 'primary'" class="management-avatar">
                <v-icon x-small color="white">mdi-shield-crown</v-icon>
              </v-avatar>
            </v-list-item-avatar>

            <v-list-item-content class="py-1">
              <v-list-item-title class="d-flex align-center text-body-2">
                <span class="text-truncate mr-1 font-weight-medium">{{ managementCompany.company.name }}</span>
                <v-icon
                  v-if="isCurrentCompanyManagement"
                  x-small
                  color="success"
                  title="Current company"
                >
                  mdi-check-circle
                </v-icon>
              </v-list-item-title>
            </v-list-item-content>

            <v-list-item-action class="my-0 ml-1">
              <v-btn
                icon
                x-small
                :disabled="!managementCompany.hasAccount"
                @click.stop="toggleFavorite(managementCompany)"
              >
                <v-icon x-small :color="managementCompany.isFavourite ? 'amber' : 'grey'">
                  {{ managementCompany.isFavourite ? 'mdi-star' : 'mdi-star-outline' }}
                </v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>

          <!-- Separator between Management and Enterprise companies -->
          <v-divider class="my-1 company-separator"></v-divider>

          <v-subheader class="enterprise-section-header compact-subheader">
            Other Companies
          </v-subheader>
        </template>

        <!-- Enterprise Companies List -->
        <v-list-item
          v-for="accessItem in enterpriseCompanies"
          :key="accessItem.id"
          @click="selectCompany(accessItem)"
          class="compact-list-item"
          :class="{ 'primary--text': accessItem.companyId === currentCompanyId }"
        >
          <v-list-item-avatar size="24" class="my-0 mr-2">
            <v-icon small :color="accessItem.companyId === currentCompanyId ? 'primary' : 'grey'">
              mdi-domain
            </v-icon>
          </v-list-item-avatar>

          <v-list-item-content class="py-1">
            <v-list-item-title class="d-flex align-center text-body-2">
              <span class="text-truncate mr-1">{{ accessItem.company.name }}</span>
              <v-icon
                v-if="accessItem.hasAccount"
                x-small
                color="success"
                title="You have access to this company"
              >
                mdi-check-circle
              </v-icon>
            </v-list-item-title>
          </v-list-item-content>

          <v-list-item-action class="my-0 ml-1">
            <v-btn
              icon
              x-small
              :disabled="!accessItem.hasAccount"
              @click.stop="toggleFavorite(accessItem)"
            >
              <v-icon x-small :color="accessItem.isFavourite ? 'amber' : 'grey'">
                {{ accessItem.isFavourite ? 'mdi-star' : 'mdi-star-outline' }}
              </v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>

        <v-list-item v-if="enterpriseCompanies.length === 0" disabled class="compact-list-item">
          <v-list-item-content class="py-1">
            <v-list-item-title class="text-center text--secondary text-body-2">
              No companies found
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
  </div>
</template>

<script>
import accessListService from '@/features/Account/service/access-list.service';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import { CompanyType, CompanyTypeExpanded } from '@/features/Share/config/enums';
import { guardDashboardExit } from '@Dashboard/composables/dashboardExitGuard';
import permissions from '@/features/Share/config/permissions';
import { userHasPermissions } from '@/utils/permissionRule';

export default {
  name: 'CompanyDropdown',
  props: {
    user: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      companySearch: '',
      availableCompanies: [],
      loadingCompanies: false,
      isLoading: false,
      targetCompany: null,
      CompanyType,
      CompanyTypeExpanded,
    };
  },
  computed: {
    currentCompanyId() {
      return this.user && this.user.company ? this.user.company.id : null;
    },
    currentCompanyName() {
      return this.user && this.user.company ? this.user.company.name : 'No Company';
    },
    isCurrentCompanyManagement() {
      return this.user?.company?.companyType === CompanyType.Management;
    },
    managementCompany() {
      return this.availableCompanies.find(
        item => item.company.companyType === CompanyType.Management
      );
    },
    currentCompanyAccessItem() {
      if (!this.currentCompanyId) return null;
      return this.availableCompanies.find(
        item => item.companyId === this.currentCompanyId
      );
    },
    enterpriseCompanies() {
      // Filter out management company AND current company (shown separately)
      const companies = this.availableCompanies.filter(
        item => item.company.companyType !== CompanyType.Management &&
                item.companyId !== this.currentCompanyId
      );
      if (!this.companySearch) {
        return companies;
      }
      const searchLower = this.companySearch.toLowerCase();
      return companies.filter(company =>
        company.company.name.toLowerCase().includes(searchLower) ||
        (company.description && company.description.toLowerCase().includes(searchLower))
      );
    },
    filteredCompanies() {
      if (!this.companySearch) {
        return this.availableCompanies;
      }

      const searchLower = this.companySearch.toLowerCase();
      return this.availableCompanies.filter(company =>
        company.company.name.toLowerCase().includes(searchLower) ||
        (company.description && company.description.toLowerCase().includes(searchLower))
      );
    },
  },
  methods: {
    async fetchAvailableCompanies() {
      // Multi-account sessions retain access granted by their management account.
      if (!this.user?.id || !(
        this.$store.getters['ACCOUNT/IS_MULTI_ACCOUNT_MODE']
        || userHasPermissions(this.user, permissions.CompaniesManagement.Impersonate)
      )) {
        this.availableCompanies = [];
        return;
      }

      this.loadingCompanies = true;
      try {
        const response = await accessListService.get();
        this.availableCompanies = response.data;
        this.availableCompanies.sort((a, b) => {
          if (a.isFavourite && !b.isFavourite) return -1;
          if (!a.isFavourite && b.isFavourite) return 1;
          return a.company.name.localeCompare(b.company.name);
        });

      } catch (error) {
        debugError('Error fetching companies:', error);
        this.$dialog.notify.error('Failed to load available companies');
      } finally {
        this.loadingCompanies = false;
      }
    },
    selectCompany(item) {
      if (item.companyId === this.currentCompanyId) {
        return;  
      }

      this.$dialog.confirm({
        text: `Switch to ${item.company.name}?`,
        title: 'Switch Company',
        actions: {
          false: 'Cancel',
          true: {
            text: 'Switch',
            color: 'primary',
            handle: () => {
              this.switchToCompany(item);
            },
          },
        },
      });
    },
    async switchToCompany(item) {
      // builder-v1: a company switch reloads the page (below), destroying an active
      // dashboard edit session. Await its exit coordinator (drain / Retry / Discard) BEFORE
      // mutating the session — while the current company's auth is still active, so the
      // captured-context layout writes can drain against the right facility. Flag off or no
      // active session resolves immediately (unchanged). false = user kept editing → abort.
      if (!(await guardDashboardExit())) return;
      try {
        // Set target company and show loading modal
        this.targetCompany = item.company;
        this.isLoading = true;
        
        const userId = this.user?.id;
        if (!userId) {
          throw new Error('User ID not available');
        }
        
        const response = await accessListService.switch( item.companyId);
        if (response.data) {
          this.$store.commit('ACCOUNT/SET_USER', response.data);
          this.$emit('company-switched', item.company);
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        debugError('Error switching company:', error);
        this.$dialog.notify.error('Failed to switch company');
        this.isLoading = false;
        this.targetCompany = null;
      }
    },
    async toggleFavorite(item) {
      try {
        const newFavoriteStatus = !item.isFavourite;
        item.isFavourite = newFavoriteStatus;
        
        const userId = this.user?.id;
        if (!userId) {
          throw new Error('User ID not available');
        }
        const data = {
          userId: userId,
          companyId: item.companyId,
          value: newFavoriteStatus
        };
        
        await accessListService.favourite(data);
        
        this.availableCompanies.sort((a, b) => {
          if (a.isFavourite && !b.isFavourite) return -1;
          if (!a.isFavourite && b.isFavourite) return 1;
          return a.company.name.localeCompare(b.company.name);
        });
        
        // Show success notification
        this.$dialog.notify.success(
          `${item.company.name} ${newFavoriteStatus ? 'added to' : 'removed from'} favorites`
        );
      } catch (error) {
        // Revert the local change if the API call fails
        item.isFavourite = !item.isFavourite;
        debugError('Error updating favorite status:', error);
        this.$dialog.notify.error('Failed to update favorite status');
      }
    }
  },
  created() {
    this.fetchAvailableCompanies();
  },
  watch: {
    user: {
      handler() {
        this.fetchAvailableCompanies();
      },
      deep: true
    }
  }
};
</script>

<style scoped>
/* =============================================================================
   Typography Enhancement
   ============================================================================= */
.company-dropdown-root {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.transparent-btn {
  background-color: transparent !important;
  color: var(--v-bg_force_white-base) !important;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.01em;
  min-width: 0 !important;
  padding: 0 !important;
  text-transform: none !important;
}

.activator-lead-icon {
  margin-right: 12px !important;
}

/* Loading Modal Styles */
.loading-modal {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.loading-modal .text-h5 {
  font-size: 18px !important;
  font-weight: 600 !important;
  letter-spacing: -0.01em !important;
}

.loading-modal .text-h6 {
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0em !important;
}

.loading-modal .text-body-2 {
  font-size: 12px !important;
  letter-spacing: 0.01em !important;
}

/* Company Switch Animation */
.company-switch-animation {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
}

.current-company-icon {
  opacity: 0.7;
  transform: scale(0.9);
  transition: all 0.5s ease;
}

.arrow-animation {
  width: 60px;
  margin: 0 15px;
  position: relative;
}

.arrow-icon {
  animation: pulse 1.5s infinite;
}

.target-company-icon {
  animation: grow 2s infinite alternate;
}

/* Custom Progress Bar */
.custom-progress-bar {
  height: 6px;
  width: 80%;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}

.progress-track {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.progress-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 30%;
  background: linear-gradient(90deg, var(--v-primary-base), var(--v-primary-lighten2));
  animation: loading 2s infinite;
  border-radius: 4px;
}

.target-company-info {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 15px 0;
}

/* Animations */
@keyframes pulse {
  0% {
    opacity: 0.6;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 0.6;
    transform: scale(0.8);
  }
}

@keyframes grow {
  0% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1.1);
  }
}

@keyframes loading {
  0% {
    left: -30%;
  }
  100% {
    left: 100%;
  }
}

/* =============================================================================
   Card & Header Typography
   ============================================================================= */
.v-card__title {
  font-size: 15px !important;
  font-weight: 600 !important;
  letter-spacing: -0.01em !important;
  color: var(--v-text-base) !important;
}

.v-card__title .text-h6 {
  font-size: 15px !important;
  font-weight: 600 !important;
  letter-spacing: -0.01em !important;
}

/* Search Field Typography */
.v-text-field input {
  font-size: 13px !important;
  letter-spacing: 0.01em !important;
}

.v-text-field input::placeholder {
  font-size: 13px !important;
  letter-spacing: 0.01em !important;
  opacity: 0.6;
}

/* Management Company Styles */
.management-back-icon-btn {
  border: 1px solid var(--v-primary-base);
  background-color: rgba(var(--v-primary-base-rgb), 0.08) !important;
}

.management-back-icon-btn:hover {
  background-color: rgba(var(--v-primary-base-rgb), 0.15) !important;
}

.management-section-header {
  font-weight: 600;
  font-size: 10px !important;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--v-primary-base);
}

.management-company-item {
  background: linear-gradient(90deg, rgba(var(--v-primary-base-rgb), 0.05), transparent);
  border-left: 3px solid var(--v-primary-base);
  margin: 4px 8px;
  border-radius: 0 8px 8px 0;
  transition: all 0.2s ease;
}

.management-company-item--active {
  background: linear-gradient(90deg, rgba(var(--v-success-base-rgb), 0.08), transparent);
  border-left-color: var(--v-success-base);
}

.management-company-item:hover {
  background: linear-gradient(90deg, rgba(var(--v-primary-base-rgb), 0.12), transparent);
}

.management-company-item--active:hover {
  background: linear-gradient(90deg, rgba(var(--v-success-base-rgb), 0.15), transparent);
}

.management-avatar {
  box-shadow: 0 2px 8px rgba(var(--v-primary-base-rgb), 0.3);
}

/* Current Company Styles */
.current-section-header {
  font-weight: 600;
  font-size: 10px !important;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--v-success-base);
}

.current-company-item {
  background: linear-gradient(90deg, rgba(var(--v-success-base-rgb), 0.08), transparent);
  border-left: 3px solid var(--v-success-base);
  margin: 4px 8px;
  border-radius: 0 8px 8px 0;
  transition: all 0.2s ease;
}

.current-company-item:hover {
  background: linear-gradient(90deg, rgba(var(--v-success-base-rgb), 0.12), transparent);
}

.current-avatar {
  box-shadow: 0 2px 8px rgba(var(--v-success-base-rgb), 0.3);
}

.company-separator {
  margin-left: 16px;
  margin-right: 16px;
  border-color: rgba(0, 0, 0, 0.08);
}

.enterprise-section-header {
  font-weight: 600;
  font-size: 10px !important;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--v-text_secondary-base);
}

/* Compact List Styles */
.compact-list {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.compact-subheader {
  height: 28px !important;
  min-height: 28px !important;
  font-size: 10px !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0 12px !important;
}

.compact-list-item {
  min-height: 36px !important;
  padding: 0 8px !important;
}

.compact-list-item .v-list-item__avatar {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.compact-chip {
  height: 16px !important;
  font-size: 9px !important;
  font-weight: 600 !important;
  letter-spacing: 0.04em;
  padding: 0 6px !important;
}

/* =============================================================================
   List Item Typography
   ============================================================================= */
.compact-list-item .v-list-item__title {
  font-size: 13px !important;
  font-weight: 500 !important;
  letter-spacing: 0.01em !important;
  color: var(--v-text-base) !important;
}

.compact-list-item .text-truncate {
  font-size: 13px !important;
  font-weight: 500 !important;
}

.compact-list-item .font-weight-medium {
  font-weight: 500 !important;
}

.management-section-header.compact-subheader,
.current-section-header.compact-subheader {
  padding: 0 12px !important;
}

.management-company-item.compact-list-item,
.current-company-item.compact-list-item {
  margin: 2px 4px;
}
</style>
